import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as testCaseApi from '../services/testCaseService';
import type { ExecuteTestCaseData, CreateTestCaseData, UpdateTestCaseData } from '../services/testCaseService';

export const testCaseKeys = {
  all: (projectId: string) => ['testCases', projectId] as const,
  detail: (projectId: string, id: string) => ['testCases', projectId, id] as const,
  history: (projectId: string, id: string) => ['testCases', projectId, id, 'history'] as const,
  needsReviewSummary: (projectId: string) => ['testCases', projectId, 'needsReviewSummary'] as const,
  envProfiles: (userId: string) => ['testEnvProfiles', userId] as const,
};

// --- Test Case hooks ---

export function useTestCases(
  projectId: string,
  params?: { specId?: string; status?: string; type?: string },
) {
  return useQuery({
    queryKey: [...testCaseKeys.all(projectId), params],
    queryFn: () => testCaseApi.fetchTestCases(projectId, params),
    enabled: !!projectId,
  });
}

export function useTestCase(projectId: string, testCaseId: string) {
  return useQuery({
    queryKey: testCaseKeys.detail(projectId, testCaseId),
    queryFn: () => testCaseApi.fetchTestCase(projectId, testCaseId),
    enabled: !!projectId && !!testCaseId,
  });
}

// CHG-008: TC 수동 생성
export function useCreateTestCase(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTestCaseData) =>
      testCaseApi.createTestCase(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testCaseKeys.all(projectId) });
    },
  });
}

// CHG-010: TC 수정
export function useUpdateTestCase(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { testCaseId: string; data: UpdateTestCaseData }) =>
      testCaseApi.updateTestCase(projectId, params.testCaseId, params.data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: testCaseKeys.all(projectId) });
      queryClient.invalidateQueries({
        queryKey: testCaseKeys.detail(projectId, vars.testCaseId),
      });
    },
  });
}

// TC 명세 간 이동
export function useMoveTestCases(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { testCaseIds: string[]; targetSpecId: string }) =>
      testCaseApi.moveTestCases(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testCaseKeys.all(projectId) });
    },
  });
}

// CHG-021: TC 검토완료
export function useReviewComplete(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (testCaseId: string) =>
      testCaseApi.reviewComplete(projectId, testCaseId),
    onSuccess: (_data, testCaseId) => {
      queryClient.invalidateQueries({ queryKey: testCaseKeys.all(projectId) });
      queryClient.invalidateQueries({
        queryKey: testCaseKeys.detail(projectId, testCaseId),
      });
    },
  });
}

// CHG-021: TC 일괄 검토완료
export function useBatchReviewComplete(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (testCaseIds: string[]) =>
      testCaseApi.batchReviewComplete(projectId, testCaseIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testCaseKeys.all(projectId) });
    },
  });
}

// CHG-022: 재검토 필요 TC 그룹핑
export function useNeedsReviewSummary(projectId: string, enabled = true) {
  return useQuery({
    queryKey: testCaseKeys.needsReviewSummary(projectId),
    queryFn: () => testCaseApi.fetchNeedsReviewSummary(projectId),
    enabled: !!projectId && enabled,
  });
}

// TC 삭제
export function useDeleteTestCase(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (testCaseId: string) =>
      testCaseApi.deleteTestCase(projectId, testCaseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testCaseKeys.all(projectId) });
    },
  });
}

export function useExecuteTestCase(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { testCaseId: string; data: ExecuteTestCaseData }) =>
      testCaseApi.executeTestCase(projectId, params.testCaseId, params.data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: testCaseKeys.all(projectId) });
      queryClient.invalidateQueries({
        queryKey: testCaseKeys.detail(projectId, vars.testCaseId),
      });
      queryClient.invalidateQueries({
        queryKey: testCaseKeys.history(projectId, vars.testCaseId),
      });
    },
  });
}

export function useExecutionHistory(projectId: string, testCaseId: string) {
  return useQuery({
    queryKey: testCaseKeys.history(projectId, testCaseId),
    queryFn: () => testCaseApi.fetchExecutionHistory(projectId, testCaseId),
    enabled: !!projectId && !!testCaseId,
  });
}

// --- Test Env Profile hooks ---

export function useTestEnvProfiles(userId: string) {
  return useQuery({
    queryKey: testCaseKeys.envProfiles(userId),
    queryFn: () => testCaseApi.fetchTestEnvProfiles(userId),
    enabled: !!userId,
  });
}

export function useCreateTestEnvProfile(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: testCaseApi.CreateTestEnvProfileData) =>
      testCaseApi.createTestEnvProfile(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testCaseKeys.envProfiles(userId) });
    },
  });
}

export function useUpdateTestEnvProfile(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { profileId: string; data: testCaseApi.UpdateTestEnvProfileData }) =>
      testCaseApi.updateTestEnvProfile(userId, params.profileId, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testCaseKeys.envProfiles(userId) });
    },
  });
}

export function useDeleteTestEnvProfile(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string) => testCaseApi.deleteTestEnvProfile(userId, profileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testCaseKeys.envProfiles(userId) });
    },
  });
}

export function useSetActiveProfiles(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profileIds: string[]) => testCaseApi.setActiveProfiles(userId, profileIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testCaseKeys.envProfiles(userId) });
    },
  });
}
